import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinTextField,
  ThinTextAreaField,
  AddButton,
  ThinDropdownWithSearch,
} from "../../../components/common/GeneralComponent";
import { fetchData, submitFormWithErrors } from "../../../utils/functions";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

const VerificationSupport = ({
  grantId,
  onNext,
  isEditMode,
  showNextOnly,
  onStoreFormData,
  onValidationChange,
  initialData,
  onGrantUpdated,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [verificationReportFile, setVerificationReportFile] = useState(null);
  const [spotCheckReportFile, setSpotCheckReportFile] = useState(null);
  const [reportTouched, setReportTouched] = useState(false);

  const validationSchema = Yup.object({
    field_officer_name: Yup.string(),
    field_verification_date: Yup.date()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      )
      .nullable(),
    field_verification_district_id: Yup.string(),
    technical_assistance_provided: Yup.boolean().required(
      "Technical Assistance status is required"
    ),
    verification_approved: Yup.boolean()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      )
      .nullable(),
    field_officer_remarks: Yup.string()
         .trim()
         .max(500, "Remarks must not exceed 500 characters")
         .when("technical_assistance_provided", {
           is: true,
           then: (schema) =>
             schema.required(
               "Remarks are required when technical assistance is provided"
             ),
           otherwise: (schema) => schema.nullable(),
         }),
  });

  const formik = useFormik({
    initialValues: {
      field_officer_name: initialData?.field_officer_name || "",
      field_verification_date: initialData?.field_verification_date || "",
      field_verification_district_id:
        initialData?.field_verification_district_id || "",
      technical_assistance_provided:
        initialData?.technical_assistance_provided ?? "",
      verification_approved: initialData?.verification_approved ?? "",
      field_officer_remarks: initialData?.field_officer_remarks || "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      await handleFormSubmit(values);
    },
  });

  useEffect(() => {
    if (initialData?.verification_report_file && !verificationReportFile) {
      setVerificationReportFile(initialData.verification_report_file);
    }
    if (initialData?.spot_check_report_file && !spotCheckReportFile) {
      setSpotCheckReportFile(initialData.spot_check_report_file);
    }
  }, [initialData]);

  const validateAndPickFile = (e) => {
    const file = e.target.files[0];
    if (!file) return null;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const maxSize = 1 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PDF or Word document");
      e.target.value = "";
      return null;
    }

    if (file.size > maxSize) {
      toast.error("File size should be less than 1MB");
      e.target.value = "";
      return null;
    }

    return file;
  };

  const handleFileChange = (e) => {
    const file = validateAndPickFile(e);
    if (file) {
      setVerificationReportFile(file);
      setReportTouched(true);
    }
  };

  const handleSpotCheckFileChange = (e) => {
    const file = validateAndPickFile(e);
    if (file) {
      setSpotCheckReportFile(file);
    }
  };

  const handleFormSubmit = async (values) => {
    setReportTouched(true);
    if (!verificationReportFile) {
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        let putBody;
        if (
          verificationReportFile instanceof File ||
          spotCheckReportFile instanceof File
        ) {
          putBody = new FormData();
          Object.entries(values).forEach(([key, val]) => {
            if (val !== null && val !== undefined) {
              putBody.append(key, val);
            }
          });
          if (verificationReportFile instanceof File) {
            putBody.append("verification_report_file", verificationReportFile);
          }
          if (spotCheckReportFile instanceof File) {
            putBody.append("spot_check_report_file", spotCheckReportFile);
          }
        } else {
          putBody = { ...values };
        }

        const response = await fetchData(
          `micro-grants/${grantId}`,
          "PUT",
          putBody,
          {},
          {},
          navigate,
          "",
          dispatch
        );

        if (response) {
          toast.success("Verification & Support details updated successfully");
          if (onGrantUpdated) {
            onGrantUpdated();
          }
        }
      } else {
        if (onStoreFormData) {
          onStoreFormData({
            ...values,
            verification_report_file: verificationReportFile,
            spot_check_report_file: spotCheckReportFile,
          });
        }
        toast.success("Verification & Support details saved locally");
        if (onNext) {
          onNext();
        }
      }
    } catch (error) {
      console.error("Error saving verification & support details:", error);
      toast.error("Failed to save verification & support details");
    } finally {
      setLoading(false);
    }
  };

  const handleNextClick = () => {
    if (loading) return;

    setReportTouched(true);
    if (!verificationReportFile) {
      return;
    }

    formik.validateForm().then((validationErrors) => {
      if (Object.keys(validationErrors).length > 0) {
        formik.setTouched(
          Object.keys(formik.values).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {})
        );
        return;
      }
      proceedWithNavigation();
    });
  };

  const proceedWithNavigation = () => {
    if (onStoreFormData) {
      onStoreFormData({
        ...formik.values,
        verification_report_file: verificationReportFile,
        spot_check_report_file: spotCheckReportFile,
      });
    }

    toast.success("Verification & Support details saved");

    if (onNext) {
      onNext();
    }
  };

  useEffect(() => {
    if (onValidationChange) {
      const timeoutId = setTimeout(() => {
        const errors = formik.validateForm();
        if (typeof errors === "object" && errors.then) {
          errors.then((validationErrors) => {
            onValidationChange(
              Object.keys(validationErrors).length === 0 &&
                !!verificationReportFile
            );
          });
        } else {
          onValidationChange(
            Object.keys(errors).length === 0 && !!verificationReportFile
          );
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [formik.values, onValidationChange, verificationReportFile]);

  useEffect(() => {
    if (onStoreFormData && formik.values && !isEditMode) {
      const timeoutId = setTimeout(() => {
        onStoreFormData({
          ...formik.values,
          verification_report_file: verificationReportFile,
          spot_check_report_file: spotCheckReportFile,
        });
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [
    formik.values,
    onStoreFormData,
    isEditMode,
    verificationReportFile,
    spotCheckReportFile,
  ]);

  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await fetchData(
          "districts",
          "GET",
          null,
          {},
          { status: 1 },
          navigate,
          "",
          dispatch
        );

        setDistricts(response?.data || []);
      } catch (error) {
        console.error("Error fetching districts:", error);
      }
    };

    fetchDistricts();
  }, []);

  return (
    <form onSubmit={(e) => {
        setReportTouched(true);
        submitFormWithErrors(formik)(e);
      }} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <ThinTextField
            label="Field Officer Name"
            name="field_officer_name"
            value={formik.values.field_officer_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.field_officer_name &&
              formik.errors.field_officer_name
            }
            placeholder="Enter Field Officer Name"
            isRequired={false}
          />
          {formik.touched.field_officer_name &&
            formik.errors.field_officer_name && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.field_officer_name}
              </div>
            )}
        </div>

        <div>
          <ThinTextField
            label="Date of Field Verification"
            name="field_verification_date"
            type="date"
            value={formik.values.field_verification_date}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.field_verification_date &&
              formik.errors.field_verification_date
            }
            isRequired={false}
          />
          {formik.touched.field_verification_date &&
            formik.errors.field_verification_date && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.field_verification_date}
              </div>
            )}
        </div>

        <div>
          <ThinDropdownWithSearch
            label="Field Verification District"
            options={districts.map((d) => ({
              value: d.district_id || d.District_Id,
              label: d.district_name || d.District_Name,
            }))}
            value={formik.values.field_verification_district_id}
            onChange={(value) =>
              formik.setFieldValue("field_verification_district_id", value)
            }
            error={
              formik.touched.field_verification_district_id &&
              formik.errors.field_verification_district_id
            }
            required={false}
          />
          {formik.touched.field_verification_district_id &&
            formik.errors.field_verification_district_id && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.field_verification_district_id}
              </div>
            )}
        </div>

        <div>
          <ThinDropdownWithSearch
            label="Technical Assistance Provided"
            options={[
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ]}
            value={formik.values.technical_assistance_provided}
            onChange={(value) =>
              formik.setFieldValue("technical_assistance_provided", value)
            }
            error={
              formik.touched.technical_assistance_provided &&
              formik.errors.technical_assistance_provided
            }
          />
          {formik.touched.technical_assistance_provided &&
            formik.errors.technical_assistance_provided && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.technical_assistance_provided}
              </div>
            )}
        </div>

        <div>
          <ThinDropdownWithSearch
            label="Approved"
            options={[
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ]}
            value={formik.values.verification_approved}
            onChange={(value) =>
              formik.setFieldValue("verification_approved", value)
            }
            error={
              formik.touched.verification_approved &&
              formik.errors.verification_approved
            }
            required={false}
          />
          {formik.touched.verification_approved &&
            formik.errors.verification_approved && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.verification_approved}
              </div>
            )}
        </div>

        {/* Verification Report Upload */}
        <div>
          <label className="text-[0.9rem] text-[#565656] mb-1 font-[InterLight] block">
            Verification Report <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            className="text-[15px] font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem] w-full"
          />
          {verificationReportFile instanceof File && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-800 flex items-center">
                <span className="mr-2">✓</span>
                Selected:{" "}
                <strong className="ml-1">{verificationReportFile.name}</strong>
              </p>
              <p className="text-xs text-green-600 mt-1">
                Size: {(verificationReportFile.size / (1024 * 1024)).toFixed(2)}{" "}
                MB
              </p>
            </div>
          )}
          {typeof verificationReportFile === "string" &&
            verificationReportFile && (
              <p className="text-sm text-blue-600 mt-1">
                Current file:{" "}
                <a
                  href={`${process.env.REACT_APP_BASE_URL}${verificationReportFile}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-800"
                >
                  View / Download
                </a>
              </p>
            )}
          <p className="text-xs text-gray-500 mt-1">
            Accepted formats: PDF, DOC, DOCX (Max size: 1MB)
          </p>
          {reportTouched && !verificationReportFile && (
            <div className="text-red-500 text-sm mt-1">
              Verification report upload is required
            </div>
          )}
        </div>

        <div>
          <label className="text-[0.9rem] text-[#565656] mb-1 font-[InterLight] block">
            Spot Check Report
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleSpotCheckFileChange}
            className="text-[15px] font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem] w-full"
          />
          {spotCheckReportFile instanceof File && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-800 flex items-center">
                <span className="mr-2">✓</span>
                Selected:{" "}
                <strong className="ml-1">{spotCheckReportFile.name}</strong>
              </p>
              <p className="text-xs text-green-600 mt-1">
                Size: {(spotCheckReportFile.size / (1024 * 1024)).toFixed(2)}{" "}
                MB
              </p>
            </div>
          )}
          {typeof spotCheckReportFile === "string" &&
            spotCheckReportFile && (
              <p className="text-sm text-blue-600 mt-1">
                Current file:{" "}
                <a
                  href={`${process.env.REACT_APP_BASE_URL}${spotCheckReportFile}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-800"
                >
                  View / Download
                </a>
              </p>
            )}
          <p className="text-xs text-gray-500 mt-1">
            Accepted formats: PDF, DOC, DOCX (Max size: 1MB) — Optional
          </p>
        </div>

        <div>
          <ThinTextAreaField
            label="Remarks by Field Officer"
            name="field_officer_remarks"
            value={formik.values.field_officer_remarks}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.field_officer_remarks &&
              formik.errors.field_officer_remarks
            }
            placeholder="Enter remarks"
            rows={4}
            maxLength={500}
          />
          <div className="text-xs text-gray-500 mt-1 text-right">
            {(formik.values.field_officer_remarks || "").length} / 500 characters
          </div>
          {formik.touched.field_officer_remarks &&
            formik.errors.field_officer_remarks && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.field_officer_remarks}
              </div>
            )}
        </div>
      </div>

      <div className="pt-4 flex justify-end gap-3">
        {showNextOnly && !isEditMode && (
          <button
            type="button"
            onClick={handleNextClick}
            className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Processing..." : "Next"}
          </button>
        )}
        {isEditMode && (
          <AddButton
            text="Update"
            type="submit"
            isLoading={loading}
            disabled={loading}
          />
        )}
      </div>
    </form>
  );
};

export default VerificationSupport;
